import { useMemo } from "react";

import { useAuthContext } from "shared/utils/hooks/useAuthContext";

import { INotification } from "../../../@types";
import {
  ICreateTicketNotificationMetadataProps,
  INotificationResultModelProps,
} from "../../../@types/createTicketNotificationTypes";
import CancelRecurrenceIcon from "../../../assets/icons/CancelRecurrenceIcon";
import RefundRequestIcon from "../../../assets/icons/RefundRequestIcon";

export const useCreateTicketNotification = (
  notification: INotification<ICreateTicketNotificationMetadataProps>,
) => {
  const { loggedUser } = useAuthContext();
  const isUserAdmin = loggedUser?.admin;

  const id = notification?.id;
  const ticket = notification?.metadata?.ticket;
  const isAuthorOfTicket = id === ticket?.id;
  const moderatorId = notification?.user_id;
  const type = notification?.type;
  const readAt = notification?.read_at;
  const createdAt = notification?.created_at;
  const user = notification?.metadata?.ticket?.user;
  const transaction = notification?.metadata?.transaction;

  const TicketIcon =
    ticket?.type === "refund" ? RefundRequestIcon : CancelRecurrenceIcon;

  const goToContentButtonText = useMemo(
    () => (isAuthorOfTicket ? "Ver detalhes" : "Ir até o ticket"),
    [isAuthorOfTicket],
  );

  const getNotificationTicketType = useMemo(
    () =>
      ticket?.type === "refund" ? "reembolso" : "cancelamento de recorrência",
    [ticket],
  );

  const getTicketLink = useMemo(() => {
    return isUserAdmin
      ? `/console/help-desk?ticket=${ticket?.id}`
      : `/orders/${transaction?.uuid_order}`;
  }, [isUserAdmin, ticket?.id, transaction?.uuid_order]);

  const notificationResult = useMemo<INotificationResultModelProps>(
    () => ({
      contentPhotoSrc: user?.photo,
      ticketReason: ticket?.reason,
      ticketLink: getTicketLink,
      ticketId: ticket?.id,

      ticketType: ticket?.type === "refund" ? "Reembolso" : "Recorrência",

      ticketValue: new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
      }).format(transaction?.amount),

      contentTitle: (
        <span>
          <b>{user?.display_name}</b> abriu um ticket de{" "}
          {getNotificationTicketType} para <b>{transaction?.group_name}</b>.
        </span>
      ),
    }),
    [user, getNotificationTicketType, transaction, ticket, getTicketLink],
  );

  return {
    id,
    user,
    type,
    ticket,
    readAt,
    createdAt,
    TicketIcon,
    transaction,
    moderatorId,
    isAuthorOfTicket,
    notificationResult,
    goToContentButtonText,
  };
};
